import type { ApiResponse } from '@shared/types'
import { PLIVOCX_URL } from 'v2/configs/constants'
import type { AppSummary, RAppSummary } from 'v2/pages/conversations/components/right-bar/types'
import { BackendServiceName, type PostService } from 'v2/types'
import type {
  AccessRecord,
  OrgAppConnectionPayload,
  RContactSummaryResponse,
  RContactViewConfigResponse,
  RExternalApp,
  RExternalOrgApp,
  ROAuthProvider,
  ROrgAppConnection,
  ROrgAppConnectionV2,
  UpsertContactViewConfigPayload,
} from 'v2/types/api/settings'
import { createService, type Service } from './create-service'

/**
 * The plivocx host is called directly (outside hodor-regional) but still gets the
 * console session token from the request interceptor, so an unconfigured host is a
 * credential-disclosure risk, not a broken feature. Fail loudly instead.
 */
export const requireConfiguredPlivoCxUrl = (url: string = PLIVOCX_URL) => {
  if (!url) {
    throw new Error(
      'REACT_APP_PLIVOCX_URL is not configured; refusing to send the session token to an unconfigured host',
    )
  }
  return url
}

export interface ExecuteActionPayload {
  action_name: string
  input: Record<string, unknown>
  organization_external_app_uuid: string
}

export interface ActionResponse {
  error?: {
    http_status_code: number
    response: string
  }
  output: unknown | null
  success: boolean
}

export interface CustomField {
  create: boolean
  edit: boolean
  field_identifier: string
  field_metadata: unknown
  title: string
  view: boolean
}

export interface BatchCustomFieldsResponse {
  fields_by_entity: Record<string, CustomField[]>
}

export interface SecretVariable {
  id: string
  name: string
  display_name?: string
  value: string
  description: string
  created_at: string
  updated_at: string
  created_by: {
    uuid: string
    name: string
  }
}

export interface AppVariables {
  [key: string]: SecretVariable[]
}

export interface SetOAuthCredentialsPayload {
  app_provider: string
  client_id: string
  client_secret: string
}

const EXTERNAL_ENDPOINTS = {
  externalApps: 'apps',
  externalOrgApps: (appId?: string, isActive?: boolean) =>
    `organization-app${appId ? `/${appId}` : ''}${isActive ? '?state=ACTIVE' : ''}`,
  externalRedirectionURL: (appId: string) => `organization-app/${appId}/oauth-redirect-url`,
  externalReconnectOrgAppConnection: (appId: string) => `organization-app/${appId}/reconnect`,
  appSummary: (appId: string, contactIdentifier: string) =>
    `apps/${appId}/contact-summary/${encodeURIComponent(contactIdentifier)}`,
  executeAction: 'execute-action',
  secretVariables: (appId: string) => `organization-app/${appId}/secret-variables`,
  appVariables: 'app-variables',
  customFields: (appId: string, customFieldEntity: string) =>
    `organization-app/${appId}/custom-fields/${customFieldEntity}`,
  resyncCustomFields: (appId: string, customFieldEntity: string) =>
    `organization-app/${appId}/custom-fields/${customFieldEntity}/resync`,
  grantAccess: (appId: string) => `organization-app/${appId}/grant-access`,
  revokeAccess: (appId: string) => `organization-app/${appId}/revoke-access`,
  accessRecords: (orgAppUuid: string) => `organization-app/${orgAppUuid}/access-records`,
  finishShopifyInstall: 'shopify-install/finish-install',
  contactViewConfig: 'contact-view-configuration',
  contactSummary: (contactIdentifier: string) =>
    `contact-summary/${encodeURIComponent(contactIdentifier)}`,
  oauthProviders: 'aci/oauth-providers',
  oauthCredentials: ['aci', 'oauth-credentials'].join('/'),
}

interface ExternalService {
  getOAuthProviders: () => Promise<ApiResponse<ROAuthProvider[]>>
  setOAuthCredentials: (payload: SetOAuthCredentialsPayload) => Promise<ApiResponse<void>>
  getExternalApps: () => Promise<ApiResponse<RExternalApp>>
  getExternalAppsV2: () => Promise<ApiResponse<RExternalApp>>
  getExternalOrgApps: (isActive?: boolean) => Promise<ApiResponse<RExternalOrgApp[]>>
  getExternalOrgAppsV2: (isActive?: boolean) => Promise<ApiResponse<RExternalOrgApp[]>>
  getExternalOrgAppsV3: (isActive?: boolean) => Promise<ApiResponse<RExternalOrgApp[]>>
  createOrgAppConnection: (
    payload: OrgAppConnectionPayload,
  ) => Promise<ApiResponse<ROrgAppConnection>>
  createOrgAppConnectionV2: (
    payload: OrgAppConnectionPayload,
  ) => Promise<ApiResponse<ROrgAppConnectionV2>>
  getRedirectionURL: (appId: string) => Promise<ApiResponse<string>>
  updateOrgAppConnection: (
    payload: OrgAppConnectionPayload,
    appId: string,
  ) => Promise<ApiResponse<ROrgAppConnection>>
  updateOrgAppConnectionV2: (
    payload: OrgAppConnectionPayload,
    appId: string,
  ) => Promise<ApiResponse<ROrgAppConnection>>
  deleteOrgAppConnection: (appId: string) => Promise<ApiResponse<void>>
  reconnectOrgAppConnection: (appId: string) => Promise<ApiResponse<ROrgAppConnectionV2>>
  getAppSummary: (
    appId: string,
    contactIdentifier: string,
  ) => Promise<ApiResponse<RAppSummary<AppSummary>>>
  executeAction: (payload: ExecuteActionPayload) => Promise<ApiResponse<ActionResponse>>
  getSecretVariables: (appId: string) => Promise<ApiResponse<SecretVariable[]>>
  getAppVariables: () => Promise<ApiResponse<{ data: AppVariables }>>
  getCustomFields: (
    appId: string,
    entities: string[],
  ) => Promise<ApiResponse<BatchCustomFieldsResponse>>
  updateCustomFields: (
    appId: string,
    fieldsByEntity: Record<string, CustomField[]>,
  ) => Promise<ApiResponse<void>>
  resyncCustomFields: (
    appId: string,
    entities: string[],
  ) => Promise<ApiResponse<BatchCustomFieldsResponse>>
  grantAccess: (appId: string, payload?: Record<string, unknown>) => Promise<ApiResponse<void>>
  revokeAccess: (appId: string, payload?: Record<string, unknown>) => Promise<ApiResponse<void>>
  finishShopifyInstall: PostService<void, { shopify_install_id: string }>
  getAccessRecords: (orgAppUuid: string) => Promise<ApiResponse<AccessRecord[]>>
  getContactViewConfig: () => Promise<ApiResponse<RContactViewConfigResponse | null>>
  upsertContactViewConfig: (
    payload: UpsertContactViewConfigPayload,
  ) => Promise<ApiResponse<RContactViewConfigResponse>>
  getContactSummary: (contactIdentifier: string) => Promise<ApiResponse<RContactSummaryResponse>>
  finishLegacyAciConnect: (orgAppUuid: string) => Promise<unknown>
}

const serviceFactory = (): ExternalService => {
  const service = createService('', true, BackendServiceName.externalApps, 'v1')
  const serviceV2 = createService('', true, BackendServiceName.externalApps, 'v2')
  const serviceV3 = createService('', true, BackendServiceName.externalApps, 'v3')
  const defaultServiceV1 = createService('', true, BackendServiceName.default, 'v1')
  // Built lazily so an unconfigured plivocx host fails at the call site (with a
  // real error the caller surfaces) instead of at module load.
  let directPlivoCxService: Service | undefined
  const getDirectPlivoCxService = () => {
    const domain = requireConfiguredPlivoCxUrl()
    directPlivoCxService ??= createService('', false, BackendServiceName.default, '', domain)
    return directPlivoCxService
  }

  return {
    getOAuthProviders: () => service.get(EXTERNAL_ENDPOINTS.oauthProviders),
    setOAuthCredentials: (payload) => service.post(EXTERNAL_ENDPOINTS.oauthCredentials, payload),
    getExternalApps: () => service.get(EXTERNAL_ENDPOINTS.externalApps),
    getExternalAppsV2: () => serviceV2.get(EXTERNAL_ENDPOINTS.externalApps),
    getExternalOrgApps: (isActive) =>
      service.get(EXTERNAL_ENDPOINTS.externalOrgApps(undefined, isActive)),
    getExternalOrgAppsV2: (isActive) =>
      serviceV2.get(EXTERNAL_ENDPOINTS.externalOrgApps(undefined, isActive)),
    getExternalOrgAppsV3: (isActive) =>
      serviceV3.get(EXTERNAL_ENDPOINTS.externalOrgApps(undefined, isActive)),
    createOrgAppConnection: (payload) =>
      service.post(EXTERNAL_ENDPOINTS.externalOrgApps(), payload),
    createOrgAppConnectionV2: (payload) =>
      serviceV2.post(EXTERNAL_ENDPOINTS.externalOrgApps(), payload),
    updateOrgAppConnection: (payload, appId) =>
      service.patch(EXTERNAL_ENDPOINTS.externalOrgApps(appId), payload),
    updateOrgAppConnectionV2: (payload, appId) =>
      serviceV2.patch(EXTERNAL_ENDPOINTS.externalOrgApps(appId), payload),
    deleteOrgAppConnection: (appId) => service.delete(EXTERNAL_ENDPOINTS.externalOrgApps(appId)),
    reconnectOrgAppConnection: (appId) =>
      serviceV2.post(EXTERNAL_ENDPOINTS.externalReconnectOrgAppConnection(appId)),
    getRedirectionURL: (appId) => service.get(EXTERNAL_ENDPOINTS.externalRedirectionURL(appId)),
    getAppSummary: (appId, contactIdentifier) =>
      service.get(EXTERNAL_ENDPOINTS.appSummary(appId, contactIdentifier), '', {
        validateStatus: (status) => status === 200 || status === 422,
      }),
    executeAction: (payload) => service.post(EXTERNAL_ENDPOINTS.executeAction, payload),
    getSecretVariables: (appId) => service.get(EXTERNAL_ENDPOINTS.secretVariables(appId)),
    getAppVariables: () => service.get(EXTERNAL_ENDPOINTS.appVariables),
    getCustomFields: (appId, entities) =>
      service.get(EXTERNAL_ENDPOINTS.customFields(appId, entities.join(','))),
    updateCustomFields: (appId, fieldsByEntity) => {
      const entities = Object.keys(fieldsByEntity).join(',')
      return service.put(EXTERNAL_ENDPOINTS.customFields(appId, entities), fieldsByEntity)
    },
    resyncCustomFields: (appId, entities) =>
      service.post(EXTERNAL_ENDPOINTS.resyncCustomFields(appId, entities.join(','))),
    grantAccess: (appId, payload) =>
      serviceV2.post(EXTERNAL_ENDPOINTS.grantAccess(appId), payload || {}),
    revokeAccess: (appId, payload) =>
      serviceV2.post(EXTERNAL_ENDPOINTS.revokeAccess(appId), payload || {}),
    finishShopifyInstall: (data) =>
      defaultServiceV1.post(EXTERNAL_ENDPOINTS.finishShopifyInstall, data),
    getAccessRecords: (orgAppUuid) => serviceV2.get(EXTERNAL_ENDPOINTS.accessRecords(orgAppUuid)),
    getContactViewConfig: () =>
      service.get(EXTERNAL_ENDPOINTS.contactViewConfig, undefined, {
        validateStatus: (status) => status === 200 || status === 404,
      }),
    upsertContactViewConfig: (payload) =>
      service.post(EXTERNAL_ENDPOINTS.contactViewConfig, payload),
    getContactSummary: (contactIdentifier) =>
      serviceV2.get(EXTERNAL_ENDPOINTS.contactSummary(contactIdentifier)),
    finishLegacyAciConnect: async (orgAppUuid) =>
      getDirectPlivoCxService().post(
        `v1/aci/finish-aci-connect?org_app_uuid=${encodeURIComponent(orgAppUuid)}`,
      ),
  }
}

export const externalService = serviceFactory()
